import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

import { environment } from 'src/environments/environment';
import { DemoDataService } from 'src/shared/services/demo-data.service';

// The lifecycle state a unit is in. Drives both the list badge and what the
// unit-detail page shows / lets you do when you click in.
export type UnitState =
  | 'Vacant'
  | 'Applications'
  | 'Signing'
  | 'Occupied'
  | 'Overdue'
  | 'Eviction';

export interface UnitPayment {
  date: string;
  amount: number;
  status: string;
  method: string;
}

export interface CollectionSettings {
  autopay: boolean;
  payment_method: string;
  due_day: number;
  late_fee: string;
  grace_days: number;
}

export interface ListingChannel {
  name: string;
  status: string;
}

export interface SigningParty {
  party: string;
  status: string;
  date?: string;
}

export interface EvictionStep {
  label: string;
  done: boolean;
  date?: string;
}

// Room-splitting (PadSplit-style) support. A unit is either rented whole
// ('entire', the default — undefined `rental_mode` means this) or split into
// individually-leasable spaces ('by_room'). Absence of every field below means
// "entire unit, monthly, as today" — nothing here changes existing units.
export type RentalMode = 'entire' | 'by_room';
export type SpaceType = 'room' | 'common';
export type OccupancyClassification = 'standard_tenancy' | 'rooming_house';
export type ScreeningPolicy = 'household_once' | 'every_member_every_move_in';

export interface SpaceListing {
  platform: string;
  platform_id?: string;
  status: 'listed' | 'paused' | 'syndicating';
}

export interface RentableSpace {
  id: number;
  name: string;                  // "Room A", "Shared kitchen"
  type: SpaceType;
  sqft?: number;
  furnished?: boolean;
  all_in?: boolean;              // utilities included in rent
  state?: UnitState;             // rooms only; common spaces have no state
  rent?: number;
  cadence?: 'weekly' | 'monthly';
  tenant_name?: string;
  tenant_email?: string;
  lease_id?: number;
  lease_start?: string;
  lease_end?: string;
  days_vacant?: number;
  application_ids?: number[];
  rent_status?: string;          // mirrors unit-level rent_status semantics
  next_due?: string;
  listings?: SpaceListing[];
}

export interface ComplianceItem {
  label: string;                 // "Rooming house license", "Fire inspection", ...
  status: 'complete' | 'action_needed' | 'pending';
  detail?: string;
  due_date?: string;
}

export interface RentalUnit {
  id: number;
  property_name: string;
  unit: string;
  address: string;
  bedrooms: number;
  bathrooms: number;
  sqft: number;
  market_rent: number;
  state: UnitState;
  status_summary: string;

  // Links this mock unit to a real account address, for demo cross-wiring
  // with address-scoped services (e.g. MarketingChannelsService). Unset by
  // default — set via linkDemoUnitToFirstAddress().
  address_id?: number;

  // Vacant
  days_vacant?: number;
  listing_channels?: ListingChannel[];

  // Applications — ids into RentalApplicationsService
  application_ids?: number[];

  // Tenant / lease (Signing, Occupied, Overdue, Eviction)
  tenant_name?: string;
  lease_start?: string;
  lease_end?: string;
  rent?: number;
  deposit?: number;

  // Signing
  signing?: SigningParty[];

  // Occupied
  rent_status?: string;
  next_due?: string;
  collection?: CollectionSettings;
  payments?: UnitPayment[];

  // Overdue / Eviction
  amount_due?: number;
  days_overdue?: number;
  late_fee_applied?: number;
  eviction_stage?: string;
  eviction_steps?: EvictionStep[];

  // Room-splitting — all optional, undefined means "entire unit" (unchanged behavior)
  rental_mode?: RentalMode;
  spaces?: RentableSpace[];
  occupancy_classification?: OccupancyClassification;
  compliance_checklist?: ComplianceItem[];
  screening_policy?: ScreeningPolicy;
}

const STANDARD_COLLECTION = (autopay: boolean): CollectionSettings => ({
  autopay,
  payment_method: 'Mercury •• 4837',
  due_day: 1,
  late_fee: '$75 after 5-day grace',
  grace_days: 5,
});

const DUMMY_UNITS: RentalUnit[] = [
  {
    id: 1,
    property_name: 'Sunset Bungalow',
    unit: 'Main House',
    address: '418 Sunset Ave, San Diego, CA',
    bedrooms: 2,
    bathrooms: 1,
    sqft: 980,
    market_rent: 2200,
    state: 'Vacant',
    status_summary: 'Vacant 9 days · Listed on 3 channels',
    days_vacant: 9,
    listing_channels: [
      { name: 'Zillow Rental Network', status: 'Active' },
      { name: 'Apartments.com', status: 'Active' },
      { name: 'Zumper', status: 'Pending' },
    ],
  },
  {
    id: 2,
    property_name: 'Maple Street Duplex',
    unit: 'Unit B',
    address: '212 Maple St, Portland, OR',
    bedrooms: 2,
    bathrooms: 1,
    sqft: 1100,
    market_rent: 2400,
    state: 'Applications',
    status_summary: '2 applications · 1 in screening',
    application_ids: [3, 1],
  },
  {
    id: 3,
    property_name: 'Riverside Townhomes',
    unit: 'Unit 12',
    address: '900 Harbor Dr, Chicago, IL',
    bedrooms: 3,
    bathrooms: 2,
    sqft: 1650,
    market_rent: 2950,
    state: 'Signing',
    status_summary: 'Awaiting tenant signature',
    tenant_name: 'Danielle Foster',
    lease_start: '2026-09-01',
    lease_end: '2027-08-31',
    rent: 2950,
    deposit: 2950,
    signing: [
      { party: 'Landlord (you)', status: 'Signed', date: '2026-07-24' },
      { party: 'Danielle Foster', status: 'Awaiting' },
    ],
  },
  {
    id: 4,
    property_name: 'Oakwood Apartments',
    unit: 'Apt 3B',
    address: '210 Linden St, Portland, OR',
    bedrooms: 1,
    bathrooms: 1,
    sqft: 720,
    market_rent: 1850,
    state: 'Occupied',
    status_summary: 'Priya Raman · Rent paid · Lease ends May 2027',
    tenant_name: 'Priya Raman',
    lease_start: '2025-06-01',
    lease_end: '2027-05-31',
    rent: 1850,
    deposit: 1850,
    rent_status: 'Paid',
    next_due: '2026-09-01',
    collection: STANDARD_COLLECTION(true),
    payments: [
      { date: '2026-08-01', amount: 1850, status: 'Paid', method: 'ACH autopay' },
      { date: '2026-07-01', amount: 1850, status: 'Paid', method: 'ACH autopay' },
      { date: '2026-06-01', amount: 1850, status: 'Paid', method: 'ACH autopay' },
      { date: '2026-05-01', amount: 1850, status: 'Paid', method: 'ACH autopay' },
      { date: '2026-04-01', amount: 1850, status: 'Paid', method: 'ACH autopay' },
    ],
  },
  {
    id: 5,
    property_name: 'Cedar Court',
    unit: 'Unit 7',
    address: '55 Cedar Ct, Austin, TX',
    bedrooms: 3,
    bathrooms: 2,
    sqft: 1480,
    market_rent: 2600,
    state: 'Occupied',
    status_summary: 'The Alvarez Family · Rent due Aug 1 · Lease ends Feb 2027',
    tenant_name: 'The Alvarez Family',
    lease_start: '2026-03-01',
    lease_end: '2027-02-28',
    rent: 2600,
    deposit: 2600,
    rent_status: 'Due',
    next_due: '2026-08-01',
    collection: STANDARD_COLLECTION(true),
    payments: [
      { date: '2026-07-01', amount: 2600, status: 'Paid', method: 'ACH autopay' },
      { date: '2026-06-01', amount: 2600, status: 'Paid', method: 'ACH autopay' },
      { date: '2026-05-01', amount: 2600, status: 'Paid', method: 'ACH autopay' },
    ],
  },
  {
    id: 6,
    property_name: 'Downtown Loft',
    unit: 'Unit 5',
    address: '77 Market St, San Francisco, CA',
    bedrooms: 0,
    bathrooms: 1,
    sqft: 560,
    market_rent: 2100,
    state: 'Overdue',
    status_summary: 'Grace Kim · $2,175 past due · 8 days',
    tenant_name: 'Grace Kim',
    lease_start: '2025-09-01',
    lease_end: '2026-08-31',
    rent: 2100,
    deposit: 2100,
    rent_status: 'Overdue',
    next_due: '2026-07-26',
    amount_due: 2175,
    days_overdue: 8,
    late_fee_applied: 75,
    collection: STANDARD_COLLECTION(false),
    payments: [
      { date: '2026-06-26', amount: 2100, status: 'Paid', method: 'Manual ACH' },
      { date: '2026-05-28', amount: 2100, status: 'Paid (late)', method: 'Manual ACH' },
    ],
  },
  {
    id: 7,
    property_name: 'Lakeview Apartments',
    unit: 'Apt 2C',
    address: '15 Lakeview Blvd, Minneapolis, MN',
    bedrooms: 2,
    bathrooms: 1,
    sqft: 1040,
    market_rent: 1950,
    state: 'Eviction',
    status_summary: 'Tyler Nguyen · Eviction: hearing Aug 12',
    tenant_name: 'Tyler Nguyen',
    lease_start: '2025-01-01',
    lease_end: '2025-12-31',
    rent: 1950,
    deposit: 1950,
    rent_status: 'Overdue',
    amount_due: 8025,
    days_overdue: 124,
    eviction_stage: 'Hearing Scheduled',
    eviction_steps: [
      { label: 'Late notice sent', done: true, date: '2026-04-06' },
      { label: 'Pay-or-quit notice served', done: true, date: '2026-04-21' },
      { label: 'Eviction filed with court', done: true, date: '2026-05-12' },
      { label: 'Hearing scheduled', done: true, date: '2026-08-12' },
      { label: 'Judgment', done: false },
      { label: 'Move-out / turnover', done: false },
    ],
  },
  {
    id: 8,
    property_name: 'Birch Street Home',
    unit: 'Main House',
    address: '340 Birch St, Denver, CO',
    bedrooms: 4,
    bathrooms: 2,
    sqft: 2100,
    market_rent: 3300,
    state: 'Vacant',
    status_summary: 'Vacant 23 days · Listed on 2 channels',
    days_vacant: 23,
    listing_channels: [
      { name: 'Zillow Rental Network', status: 'Active' },
      { name: 'Apartments.com', status: 'Active' },
    ],
  },
  // --- Room-splitting showcase: 3 of 4 rooms occupied, 1 vacant w/ waitlist ---
  {
    id: 9,
    property_name: 'Palmetto House',
    unit: 'Main House',
    address: '1124 Palmetto Ave, Charleston, SC',
    bedrooms: 4,
    bathrooms: 2,
    sqft: 1950,
    market_rent: 2200,
    state: 'Occupied',
    status_summary: '3 of 4 rooms occupied · Room D vacant 6 days',
    rental_mode: 'by_room',
    occupancy_classification: 'rooming_house',
    screening_policy: 'every_member_every_move_in',
    compliance_checklist: [
      { label: 'Rooming house license', status: 'complete', detail: 'Charleston County, renewed', due_date: '2027-01-15' },
      { label: 'Fire/life-safety inspection', status: 'pending', detail: 'Annual inspection scheduled', due_date: '2026-08-18' },
      { label: 'Egress window compliance', status: 'complete' },
      { label: 'Shared smoke/CO detectors', status: 'complete', detail: 'Serviced 2026-05-01' },
    ],
    spaces: [
      {
        id: 901,
        name: 'Room A',
        type: 'room',
        sqft: 130,
        furnished: true,
        all_in: true,
        state: 'Occupied',
        rent: 225,
        cadence: 'weekly',
        tenant_name: 'Marcus Whitfield',
        tenant_email: 'marcus.whitfield@example.com',
        lease_id: 5,
        lease_start: '2026-05-04',
        rent_status: 'Due',
        next_due: '2026-08-03',
      },
      {
        id: 902,
        name: 'Room B',
        type: 'room',
        sqft: 145,
        furnished: true,
        all_in: true,
        state: 'Occupied',
        rent: 780,
        cadence: 'monthly',
        tenant_name: 'Keisha Boone',
        tenant_email: 'keisha.boone@example.com',
        lease_id: 6,
        lease_start: '2026-02-01',
        lease_end: '2027-01-31',
        rent_status: 'Paid',
        next_due: '2026-09-01',
      },
      {
        id: 903,
        name: 'Room C',
        type: 'room',
        sqft: 150,
        furnished: true,
        all_in: true,
        state: 'Occupied',
        rent: 825,
        cadence: 'monthly',
        tenant_name: 'Felix Ostrander',
        tenant_email: 'felix.ostrander@example.com',
        lease_id: 7,
        lease_start: '2026-01-15',
        lease_end: '2027-01-14',
        rent_status: 'Paid',
        next_due: '2026-09-01',
      },
      {
        id: 904,
        name: 'Room D',
        type: 'room',
        sqft: 120,
        furnished: true,
        all_in: true,
        state: 'Vacant',
        rent: 750,
        cadence: 'monthly',
        days_vacant: 6,
        application_ids: [7, 8],
        listings: [
          { platform: 'padsplit', platform_id: 'PS-88214', status: 'listed' },
          { platform: 'furnished_finder', status: 'syndicating' },
        ],
      },
      { id: 905, name: 'Shared kitchen', type: 'common', sqft: 180, furnished: true, all_in: true },
      { id: 906, name: 'Living room', type: 'common', sqft: 220, furnished: true, all_in: true },
    ],
  },
  // --- Room-splitting compliance/edge case: license overdue, one room overdue ---
  {
    id: 10,
    property_name: 'Ferncreek Rooming House',
    unit: 'Main House',
    address: '68 Ferncreek Ln, Columbus, OH',
    bedrooms: 3,
    bathrooms: 1,
    sqft: 1400,
    market_rent: 1650,
    state: 'Overdue',
    status_summary: '2 of 3 rooms occupied · Room 2 rent overdue',
    rental_mode: 'by_room',
    occupancy_classification: 'rooming_house',
    screening_policy: 'every_member_every_move_in',
    compliance_checklist: [
      { label: 'Rooming house license', status: 'action_needed', detail: 'Renewal overdue since 2026-06-01' },
      { label: 'Fire/life-safety inspection', status: 'complete', detail: 'Passed 2026-03-10' },
      { label: 'Shared smoke/CO detectors', status: 'complete' },
    ],
    spaces: [
      {
        id: 1001,
        name: 'Room 1',
        type: 'room',
        sqft: 140,
        furnished: true,
        all_in: true,
        state: 'Occupied',
        rent: 700,
        cadence: 'monthly',
        tenant_name: 'Renata Cho',
        tenant_email: 'renata.cho@example.com',
        lease_id: 8,
        lease_start: '2025-11-01',
        lease_end: '2026-10-31',
        rent_status: 'Paid',
        next_due: '2026-09-01',
        listings: [
          { platform: 'padsplit', platform_id: 'PS-71130', status: 'paused' },
        ],
      },
      {
        id: 1002,
        name: 'Room 2',
        type: 'room',
        sqft: 130,
        furnished: true,
        all_in: true,
        state: 'Overdue',
        rent: 675,
        cadence: 'monthly',
        tenant_name: 'Dwayne Pruitt',
        tenant_email: 'dwayne.pruitt@example.com',
        lease_id: 9,
        lease_start: '2025-08-01',
        lease_end: '2027-07-31',
        rent_status: 'Overdue',
        next_due: '2026-07-01',
      },
      {
        id: 1003,
        name: 'Room 3',
        type: 'room',
        sqft: 120,
        furnished: true,
        all_in: true,
        state: 'Applications',
        rent: 650,
        cadence: 'monthly',
        days_vacant: 14,
        application_ids: [9],
        listings: [
          { platform: 'padsplit', platform_id: 'PS-90417', status: 'listed' },
        ],
      },
      { id: 1004, name: 'Shared kitchen', type: 'common', sqft: 150, furnished: true, all_in: true },
    ],
  },
];

@Injectable({
  providedIn: 'root'
})
export class RentalUnitsService {

  private http = inject(HttpClient);
  private demoData = inject(DemoDataService);
  private useDummyData = true;

  // The leasing backend isn't live yet, so a real account has no rental units — showing
  // the demo showcase (or grafting its rooms onto real addresses) would be fabricating
  // their data. The demo account is the only place the sample pipeline belongs.
  private get showsDummyData(): boolean {
    return this.useDummyData && this.demoData.shouldServeTemplates();
  }

  async getUnits(): Promise<RentalUnit[]> {
    if (this.useDummyData) {
      return this.showsDummyData ? DUMMY_UNITS : [];
    }
    return firstValueFrom(
      this.http.get<RentalUnit[]>(`${environment.api_url}api/v1/customer/rental-units`)
    );
  }

  async getUnit(id: number): Promise<RentalUnit | undefined> {
    if (this.useDummyData) {
      return this.showsDummyData ? DUMMY_UNITS.find(u => u.id === id) : undefined;
    }
    return firstValueFrom(
      this.http.get<RentalUnit>(`${environment.api_url}api/v1/customer/rental-units/${id}`)
    );
  }

  // States in pipeline order, for filter chips / counts.
  readonly states: UnitState[] = ['Vacant', 'Applications', 'Signing', 'Occupied', 'Overdue', 'Eviction'];

  // Widened to accept the derived non-UnitState display value from derivedState()
  // ('Partially occupied') so callers can color from whatever state is actually shown.
  stateColor(state: UnitState | string): string {
    switch (state) {
      case 'Vacant': return '#9E9E9E';
      case 'Applications': return '#2196F3';
      case 'Signing': return '#F9A825';
      case 'Occupied': return '#2E7D32';
      case 'Overdue': return '#EF6C00';
      case 'Eviction': return '#C62828';
      case 'Partially occupied': return '#0277BD';
      default: return '#1E2125';
    }
  }

  // ---- Room-splitting helpers (pure functions — safe to call from templates via a pipe) ----

  isByRoom(unit: RentalUnit): boolean {
    return unit.rental_mode === 'by_room';
  }

  private rooms(unit: RentalUnit): RentableSpace[] {
    return (unit.spaces || []).filter(s => s.type === 'room');
  }

  occupancySummary(unit: RentalUnit): { occupied: number; total: number; label: string } {
    const rooms = this.rooms(unit);
    const occupied = rooms.filter(r => r.state === 'Occupied' || r.state === 'Overdue' || r.state === 'Eviction').length;
    const total = rooms.length;
    const label = total ? `${occupied} of ${total} room${total === 1 ? '' : 's'} occupied` : '';
    return { occupied, total, label };
  }

  // Rolls up per-room state into a single display state for split units.
  // Entire units simply surface their own `state` — unchanged behavior.
  derivedState(unit: RentalUnit): string {
    if (!this.isByRoom(unit)) { return unit.state; }
    const rooms = this.rooms(unit);
    if (!rooms.length) { return unit.state; }
    if (rooms.some(r => r.state === 'Eviction')) { return 'Eviction'; }
    if (rooms.some(r => r.state === 'Overdue')) { return 'Overdue'; }
    const occupied = rooms.filter(r => r.state === 'Occupied').length;
    if (occupied === 0) { return 'Vacant'; }
    if (occupied === rooms.length) { return 'Occupied'; }
    return 'Partially occupied';
  }

  // Sums room rents normalized to monthly (weekly cadence × 52 / 12).
  combinedRoomRent(unit: RentalUnit): number {
    return this.rooms(unit).reduce((sum, r) => {
      if (!r.rent) { return sum; }
      const monthly = r.cadence === 'weekly' ? Math.round((r.rent * 52) / 12) : r.rent;
      return sum + monthly;
    }, 0);
  }

  yieldDelta(unit: RentalUnit): number {
    return this.combinedRoomRent(unit) - unit.market_rent;
  }

  // What the pipeline state filter checks: entire units match their own state;
  // split units match if any room is currently in that state.
  matchesStateFilter(unit: RentalUnit, state: UnitState): boolean {
    if (!this.isByRoom(unit)) { return unit.state === state; }
    return this.rooms(unit).some(r => r.state === state);
  }

  // Case-insensitive lookup of the room a tenant email belongs to, across all
  // by-room units. Shared by screening-detail (single lookup) and callers that
  // need the unit context alongside the room (e.g. property name for a label).
  async findSpaceByTenantEmail(email: string): Promise<{ unit: RentalUnit; space: RentableSpace } | null> {
    const units = await this.getUnits();
    const target = email.toLowerCase();
    for (const unit of units) {
      if (!this.isByRoom(unit)) { continue; }
      const space = (unit.spaces || []).find(s => s.tenant_email?.toLowerCase() === target);
      if (space) { return { unit, space }; }
    }
    return null;
  }

  // Same traversal as findSpaceByTenantEmail, but builds a full email -> room-name
  // map in one pass — for callers (e.g. screenings-tab) that need to resolve many
  // emails at once rather than looking up one at a time.
  roomContextByEmail(units: RentalUnit[]): Map<string, string> {
    const map = new Map<string, string>();
    for (const unit of units) {
      if (!this.isByRoom(unit)) { continue; }
      for (const space of unit.spaces || []) {
        if (space.tenant_email) { map.set(space.tenant_email.toLowerCase(), space.name); }
      }
    }
    return map;
  }

  // Default rooming-house compliance checklist — used only when converting a
  // unit that doesn't already carry one (e.g. re-converting after a revert).
  private defaultComplianceChecklist(): ComplianceItem[] {
    return [
      { label: 'Rooming house license', status: 'pending' },
      { label: 'Fire/life-safety inspection', status: 'pending' },
      { label: 'Egress window compliance', status: 'pending' },
      { label: 'Shared smoke/CO detectors', status: 'pending' },
    ];
  }

  // ---- Room-splitting mutations (dummy-data convention: mutate DUMMY_UNITS in
  // place when useDummyData; real-API path mirrors existing methods) ----

  async convertToByRoom(
    unitId: number,
    config: { occupancy_classification: OccupancyClassification; spaces: RentableSpace[] }
  ): Promise<void> {
    if (this.useDummyData) {
      const unit = DUMMY_UNITS.find(u => u.id === unitId);
      if (!unit) { return; }
      unit.rental_mode = 'by_room';
      unit.occupancy_classification = config.occupancy_classification;
      unit.screening_policy = 'every_member_every_move_in';
      unit.spaces = config.spaces;
      if (config.occupancy_classification === 'rooming_house' && !unit.compliance_checklist) {
        unit.compliance_checklist = this.defaultComplianceChecklist();
      }
      return;
    }
    await firstValueFrom(
      this.http.post(`${environment.api_url}api/v1/customer/rental-units/${unitId}/convert-to-by-room`, config)
    );
  }

  async updateSpaces(unitId: number, spaces: RentableSpace[]): Promise<void> {
    if (this.useDummyData) {
      const unit = DUMMY_UNITS.find(u => u.id === unitId);
      if (!unit) { return; }
      unit.spaces = spaces;
      return;
    }
    await firstValueFrom(
      this.http.post(`${environment.api_url}api/v1/customer/rental-units/${unitId}/spaces`, { spaces })
    );
  }

  async revertToEntire(unitId: number): Promise<{ ok: boolean; error?: string }> {
    if (this.useDummyData) {
      const unit = DUMMY_UNITS.find(u => u.id === unitId);
      if (!unit) { return { ok: false, error: 'Unit not found.' }; }
      const blockingRoom = this.rooms(unit).find(r =>
        r.state === 'Occupied' || r.state === 'Overdue' || r.state === 'Eviction' || !!r.lease_id
      );
      if (blockingRoom) {
        return { ok: false, error: `${blockingRoom.name} still has a tenant or active lease. Move them out before converting back to an entire unit.` };
      }
      unit.rental_mode = undefined;
      unit.spaces = undefined;
      unit.occupancy_classification = undefined;
      unit.screening_policy = undefined;
      unit.compliance_checklist = undefined;
      return { ok: true };
    }
    return firstValueFrom(
      this.http.post<{ ok: boolean; error?: string }>(`${environment.api_url}api/v1/customer/rental-units/${unitId}/revert-to-entire`, {})
    );
  }

  // ---- Room listing mutations (same dummy/real convention as above) ----

  async addRoomListing(unitId: number, spaceId: number, data: { platform: string; platform_id?: string }): Promise<SpaceListing> {
    if (this.useDummyData) {
      const unit = DUMMY_UNITS.find(u => u.id === unitId);
      const space = unit?.spaces?.find(s => s.id === spaceId);
      if (!space) { throw new Error('Space not found.'); }
      const listing: SpaceListing = { platform: data.platform, platform_id: data.platform_id, status: 'syndicating' };
      space.listings = [...(space.listings || []), listing];
      return listing;
    }
    return firstValueFrom(
      this.http.post<SpaceListing>(`${environment.api_url}api/v1/customer/rental-units/${unitId}/spaces/${spaceId}/listings`, data)
    );
  }

  async updateRoomListing(unitId: number, spaceId: number, platform: string, changes: Partial<SpaceListing>): Promise<SpaceListing | null> {
    if (this.useDummyData) {
      const unit = DUMMY_UNITS.find(u => u.id === unitId);
      const space = unit?.spaces?.find(s => s.id === spaceId);
      const listing = space?.listings?.find(l => l.platform === platform);
      if (!listing) { return null; }
      Object.assign(listing, changes);
      return listing;
    }
    return firstValueFrom(
      this.http.post<SpaceListing | null>(`${environment.api_url}api/v1/customer/rental-units/${unitId}/spaces/${spaceId}/listings/${platform}`, changes)
    );
  }

  // ---- Address-picker bridge (demo cross-wiring with real-HTTP account addresses) ----

  // Reads spaces for the by-room units — used by callers that need to expand
  // an address picker or otherwise enumerate rentable rooms.
  async getByRoomUnits(): Promise<RentalUnit[]> {
    const units = await this.getUnits();
    return units.filter(u => this.isByRoom(u));
  }

  // Links the demo showcase units to the account's real addresses (Palmetto
  // House → first, Ferncreek → second when one exists), so their rooms can
  // appear under real properties in address-scoped UIs. Idempotent — no-op
  // per unit once its link is set, or outside dummy mode.
  linkDemoUnitToFirstAddress(addressItems: { viewValue: string; value: number }[]): void {
    if (!this.showsDummyData) { return; }
    const realItems = addressItems.filter(item => item.value > 0);
    if (!realItems.length) { return; }
    const links: [number, number][] = [[9, 0], [10, 1]];
    links.forEach(([unitId, addressIndex]) => {
      const unit = DUMMY_UNITS.find(u => u.id === unitId);
      const item = realItems[addressIndex];
      if (!unit || unit.address_id || !item) { return; }
      unit.address_id = item.value;
    });
  }

  // Finds the mock unit linked to a real address, if any. Used by property-page
  // surfaces that need to know whether an address is already tracked as a
  // rental unit (and if so, whether it's split into rooms) without creating one.
  async getUnitForAddress(addressId: number): Promise<RentalUnit | undefined> {
    if (this.useDummyData) {
      const units = await this.getUnits();
      return units.find(u => u.address_id === addressId);
    }
    return firstValueFrom(
      this.http.get<RentalUnit | undefined>(`${environment.api_url}api/v1/customer/rental-units/for-address/${addressId}`)
    );
  }

  // Returns the unit linked to addressId, creating a plausible entire-mode one
  // (and linking it) if the address isn't tracked as a rental unit yet. This is
  // how a property page "activates" leasing for an address on first use (e.g.
  // clicking "Split into rooms") — the new unit then also shows up in the
  // Leasing pipeline (getUnits), which is intended.
  async getOrCreateUnitForAddress(addressId: number, propertyName: string, details?: { bedrooms?: number }): Promise<RentalUnit> {
    if (this.useDummyData) {
      const existing = DUMMY_UNITS.find(u => u.address_id === addressId);
      if (existing) { return existing; }
      const newUnit: RentalUnit = {
        id: Math.max(...DUMMY_UNITS.map(u => u.id)) + 1,
        property_name: propertyName,
        unit: '',
        address: '',
        bedrooms: details?.bedrooms ?? 4,
        bathrooms: 2,
        sqft: 0,
        market_rent: 2400,
        state: 'Vacant',
        status_summary: 'Vacant · Not yet listed',
        days_vacant: 0,
        listing_channels: [],
        address_id: addressId,
      };
      DUMMY_UNITS.push(newUnit);
      return newUnit;
    }
    return firstValueFrom(
      this.http.post<RentalUnit>(`${environment.api_url}api/v1/customer/rental-units/find-or-create`, { address_id: addressId, property_name: propertyName, ...details })
    );
  }

  // Expands an address picker's items with child rows for by-room units linked
  // to a real address — one per room space, indented under the parent address.
  async expandAddressPickerItems(items: { viewValue: string; value: number }[]): Promise<{ viewValue: string; value: number }[]> {
    this.linkDemoUnitToFirstAddress(items);
    const byRoomUnits = await this.getByRoomUnits();
    const linkedUnits = byRoomUnits.filter(u => !!u.address_id);
    if (!linkedUnits.length) { return items; }
    const expanded: { viewValue: string; value: number }[] = [];
    for (const item of items) {
      expanded.push(item);
      const unit = linkedUnits.find(u => u.address_id === item.value);
      if (!unit) { continue; }
      for (const space of (unit.spaces || []).filter(s => s.type === 'room')) {
        expanded.push({ viewValue: `· ${space.name}`, value: roomPickerValue(unit.id, space.id) });
      }
    }
    return expanded;
  }
}

// ---- Room picker value encoding (standalone — no service state needed) ----
// Encodes a (unitId, spaceId) pair as a single negative number so room options
// can share a value namespace with real address ids (always positive) in a
// flat picker list.

export function roomPickerValue(unitId: number, spaceId: number): number {
  return -(unitId * 100000 + spaceId);
}

export function isRoomPickerValue(value: number): boolean {
  // Room encodings start at -(1*100000 + 1); small negatives like the schedule
  // page's -1 "All Properties" sentinel must not match.
  return value <= -100001;
}

export function parseRoomPickerValue(value: number): { unitId: number; spaceId: number } | null {
  if (!isRoomPickerValue(value)) { return null; }
  const encoded = -value;
  const spaceId = encoded % 100000;
  const unitId = (encoded - spaceId) / 100000;
  return { unitId, spaceId };
}
